// ═══════════════════════════════════════════════════════════════
// Feature Landing Pages — SEO/marketing pages for in-app features
// Each exported as a separate component for routing
// ═══════════════════════════════════════════════════════════════

import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, Heart, Sparkles, FlaskConical, Eye, Layout,
  Check, Shield, Users, Mail, BarChart3, TrendingUp,
  Target, Zap, Globe, Trophy, AlertTriangle, Lightbulb,
  FileText, Calendar, Search, Palette, ChevronRight,
} from "lucide-react";

// ── Shared components ──

function FeatureHero({ badge, title, highlight, description, icon: Icon }: {
  badge: string; title: string; highlight: string; description: string; icon: any;
}) {
  return (
    <section className="pt-16 pb-12 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
          <Icon size={13} className="text-coral" /> {badge}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
          {title} <span className="text-coral">{highlight}</span>
        </h1>
        <p className="text-white/50 text-lg max-w-xl mx-auto mb-8 leading-relaxed">{description}</p>
        <Link to="/register"
          className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20">
          Start Free <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

function FeatureGrid({ items }: { items: { title: string; desc: string; icon: any }[] }) {
  return (
    <div className="grid md:grid-cols-3 gap-5">
      {items.map(item => (
        <div key={item.title} className="bg-paper rounded-xl p-5 border border-sand text-center">
          <item.icon size={22} className="mx-auto text-coral mb-3" />
          <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.title}</h3>
          <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
        </div>
      ))}
    </div>
  );
}

function FeatureCta({ title, desc }: { title: string; desc: string }) {
  return (
    <section className="py-16 bg-ink text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
      <div className="max-w-2xl mx-auto px-6 relative z-10">
        <h2 className="font-display text-3xl font-bold text-white mb-3">{title}</h2>
        <p className="text-white/50 text-base mb-8 leading-relaxed">{desc}</p>
        <div className="flex justify-center mb-6"><LeadCaptureBox /></div>
        <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
      </div>
    </section>
  );
}

function CheckList({ items }: { items: string[] }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-sm text-slate">
          <Check size={16} className="text-teal flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 1. EMAIL HEALTH DASHBOARD
// ══════════════════════════════════════════════════════════════

export function EmailHealthLandingPage() {
  return (
    <>
      <SeoHead
        title="Email Health Dashboard — Monitor Your Sender Reputation | MoreMail"
        description="See your email program's vital signs in one place. Open rates, bounce rates, sender reputation, and engagement scores — with actionable recommendations."
        canonical={`${SITE_URL}/email-health-dashboard`}
        keywords="email health dashboard, sender reputation monitor, email deliverability dashboard, bounce rate monitor, email engagement dashboard, email program health"
        ogTitle="Email Health Dashboard — Your Email Program's Vital Signs | MoreMail"
        ogDescription="Monitor open rates, bounce rates, sender reputation, and engagement. Get an A-F grade and personalized recommendations."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Email Health Dashboard", applicationCategory: "BusinessApplication", description: "Monitor your email sender reputation, open rates, bounce rates, and list engagement in one dashboard.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeatureHero badge="Email Intelligence" icon={Heart}
        title="Your Email Program's" highlight="Vital Signs"
        description="One dashboard for everything that matters: open rates, bounce rates, sender reputation, and list engagement. Know exactly where you stand." />

      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Four Metrics That <span className="text-coral">Matter Most</span>
          </h2>
          <FeatureGrid items={[
            { title: "Open Rate Health", desc: "See if your open rates are above, at, or below industry benchmarks. Get specific tips to improve.", icon: Mail },
            { title: "Bounce Rate Monitor", desc: "Track your bounce rate in real-time. Get alerted before it damages your sender reputation.", icon: Shield },
            { title: "Sender Reputation Score", desc: "A 0-100 score based on your sending history. See exactly what's helping and hurting your reputation.", icon: Heart },
            { title: "Engagement Tiers", desc: "See how many subscribers are hot, warm, cold, or new. Know when to run re-engagement campaigns.", icon: Users },
            { title: "Smart Recommendations", desc: "Personalized suggestions based on your data — not generic advice. Every recommendation links to an action.", icon: Lightbulb },
            { title: "Overall Grade (A-F)", desc: "One letter grade that summarizes your email program's health. Track progress over time.", icon: BarChart3 },
          ]} />
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-6">How It Works</h2>
          <CheckList items={[
            "Aggregates data from your campaigns, subscriber list, and deliverability tools automatically",
            "Calculates a weighted health score across open rate, bounce rate, reputation, and engagement",
            "Generates personalized recommendations with direct links to fix each issue",
            "Updates in real-time as you send campaigns and your list evolves",
          ]} />
        </div>
      </section>

      <FeatureCta title="Know Your Email Health" desc="Stop guessing. See exactly how your email program is performing and what to fix first." />
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// 2. SMART RECOMMENDATIONS
// ══════════════════════════════════════════════════════════════

export function RecommendationsLandingPage() {
  return (
    <>
      <SeoHead
        title="Smart Email Recommendations — AI-Powered Insights for Your Campaigns | MoreMail"
        description="Get personalized, data-driven recommendations for your email program. MoreMail analyzes your account and tells you exactly what to improve — with one-click actions."
        canonical={`${SITE_URL}/smart-recommendations`}
        keywords="email marketing recommendations, AI email insights, email optimization suggestions, email campaign recommendations, smart email tips"
        ogTitle="Smart Email Recommendations — AI Tells You What to Fix | MoreMail"
        ogDescription="MoreMail analyzes your campaigns, subscribers, and deliverability — then gives you prioritized, actionable recommendations."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Smart Email Recommendations", applicationCategory: "BusinessApplication", description: "AI-powered personalized recommendations for your email marketing program.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeatureHero badge="AI-Powered Insights" icon={Sparkles}
        title="Personalized" highlight="Recommendations"
        description="MoreMail analyzes your campaigns, subscribers, automations, and deliverability data — then tells you exactly what to do next. Every suggestion is actionable." />

      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            What Gets <span className="text-coral">Analyzed</span>
          </h2>
          <FeatureGrid items={[
            { title: "Setup Gaps", desc: "Missing tracking domain? No automations? We catch the setup steps you might have skipped.", icon: Target },
            { title: "Deliverability Risks", desc: "High bounce rates, invalid emails, missing warmup — issues that threaten your inbox placement.", icon: Shield },
            { title: "Engagement Drops", desc: "Low open rates, cold subscribers outweighing engaged ones, long gaps between sends.", icon: TrendingUp },
            { title: "Growth Opportunities", desc: "Draft campaigns sitting unsent, automations in draft mode, untapped audience segments.", icon: Zap },
            { title: "Revenue Leaks", desc: "Sending emails without revenue tracking means you can't measure ROI. We'll flag that.", icon: BarChart3 },
            { title: "Priority Ranking", desc: "Every recommendation is ranked high, medium, or low. Fix the urgent ones first.", icon: AlertTriangle },
          ]} />
        </div>
      </section>

      <FeatureCta title="Stop Guessing, Start Optimizing"
        desc="16 recommendation rules analyze your entire account. Get a prioritized action plan updated in real-time." />
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// 3. A/B TEST ARCHIVE
// ══════════════════════════════════════════════════════════════

export function AbTestLandingPage() {
  return (
    <>
      <SeoHead
        title="A/B Test Archive & Insights — Learn What Works for Your Audience | MoreMail"
        description="Every A/B test you run is saved with full results. See winning subject lines, variant comparisons, uplift percentages, and pattern insights over time."
        canonical={`${SITE_URL}/ab-test-insights`}
        keywords="email AB test results, AB test archive, email split test insights, subject line testing results, email AB test analytics, email optimization data"
        ogTitle="A/B Test Archive — See What Works for Your Audience | MoreMail"
        ogDescription="Full variant comparison, winner detection, uplift tracking, and pattern insights from every A/B test you run."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "A/B Test Archive & Insights", applicationCategory: "BusinessApplication", description: "Historical A/B test results with variant comparisons, winner analysis, and pattern detection.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeatureHero badge="Testing & Optimization" icon={FlaskConical}
        title="A/B Test" highlight="Archive & Insights"
        description="Every test tells a story. MoreMail saves every A/B test result with full variant data, winner analysis, and pattern detection. Learn what works for YOUR audience." />

      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Beyond Single <span className="text-coral">Test Results</span>
          </h2>
          <FeatureGrid items={[
            { title: "Full Variant Comparison", desc: "Side-by-side open rates, click rates, and bounces for every variant. Visual bar charts make winners obvious.", icon: BarChart3 },
            { title: "Winner Detection", desc: "Automatic winner identification with uplift percentage. See exactly how much better the winner performed.", icon: Trophy },
            { title: "Pattern Insights", desc: "After 2+ tests, we detect patterns: do numbers win? Questions? Emojis? Short subjects? Your data decides.", icon: Lightbulb },
            { title: "Historical Archive", desc: "Every test is saved permanently. Scroll back through your testing history to inform future campaigns.", icon: Calendar },
            { title: "Average Uplift Tracking", desc: "See your cumulative uplift from A/B testing. Quantify how much testing has improved your results.", icon: TrendingUp },
            { title: "One-Click Testing", desc: "Add subject line variants when creating any campaign. No complex setup — just type alternatives and send.", icon: Zap },
          ]} />
        </div>
      </section>

      <FeatureCta title="Every Test Makes You Smarter"
        desc="Build a library of insights about what resonates with your audience. The more you test, the better your pattern detection gets." />
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// 4. COMPETITOR EMAIL TRACKER
// ══════════════════════════════════════════════════════════════

export function CompetitorTrackerLandingPage() {
  return (
    <>
      <SeoHead
        title="Competitor Email Tracker — See What Your Competitors Are Sending | MoreMail"
        description="Monitor your competitors' email marketing. See their subject lines, send frequency, CTAs, and content strategy. All captured automatically."
        canonical={`${SITE_URL}/competitor-email-tracker`}
        keywords="competitor email tracker, email competitive intelligence, competitor email analysis, email marketing spy tool, competitor newsletter monitor, email competitor research"
        ogTitle="Competitor Email Tracker — Spy on Their Email Strategy | MoreMail"
        ogDescription="Monitor up to 10 competitors. See their subject lines, send patterns, CTAs, and full email content — captured automatically."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Competitor Email Tracker", applicationCategory: "BusinessApplication", description: "Monitor competitor email marketing campaigns — subject lines, send frequency, CTAs, and content.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeatureHero badge="Competitive Intelligence" icon={Eye}
        title="See What Competitors" highlight="Are Sending"
        description="Subscribe to your competitors' newsletters with a monitoring email. MoreMail captures and analyzes every email they send — subject lines, content, CTAs, and send patterns." />

      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Competitive <span className="text-coral">Intelligence</span>
          </h2>
          <FeatureGrid items={[
            { title: "Email Capture", desc: "Every email your competitors send is captured and stored — subject line, body, images, links, and CTAs.", icon: Mail },
            { title: "Send Pattern Analysis", desc: "See when competitors send — day of week, time of day. Discover their cadence and adjust yours.", icon: Calendar },
            { title: "Subject Line Research", desc: "Browse their subject lines for inspiration. See average length, patterns, and what formulas they use.", icon: FileText },
            { title: "CTA Analysis", desc: "Track which CTAs competitors use most. 'Buy Now' vs 'Learn More' vs 'Get Started' — see what they favor.", icon: Target },
            { title: "Cross-Competitor View", desc: "Compare up to 10 competitors side by side. Word count, link count, image usage, and sending frequency.", icon: BarChart3 },
            { title: "Full Email Preview", desc: "Read any captured email in a sandboxed viewer. See exactly what their subscribers see.", icon: Eye },
          ]} />
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-6">How It Works</h2>
          <CheckList items={[
            "Add a competitor by name and domain (e.g. mailchimp.com)",
            "Subscribe to their newsletter using any email address you control",
            "MoreMail detects emails from their domain and captures them automatically",
            "Browse captured emails, analyze patterns, and steal inspiration legally",
          ]} />
        </div>
      </section>

      <FeatureCta title="Know Your Competition"
        desc="Track up to 10 competitors. See their strategy, steal their best ideas, and stay one step ahead." />
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// 5. LANDING PAGE BUILDER
// ══════════════════════════════════════════════════════════════

export function LandingPagesLandingPage() {
  return (
    <>
      <SeoHead
        title="Landing Page Builder — Create Opt-In Pages That Convert | MoreMail"
        description="Build beautiful opt-in pages in minutes. 6 templates, custom colors, form builder, and automatic subscriber tagging. Leads go straight into your email list."
        canonical={`${SITE_URL}/landing-page-builder`}
        keywords="email landing page builder, opt-in page builder, lead capture page creator, email signup page, landing page for email marketing, free landing page builder"
        ogTitle="Landing Page Builder — Opt-In Pages in 60 Seconds | MoreMail"
        ogDescription="6 templates, custom styling, form builder, auto-tagging. Build landing pages that feed directly into your email list."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Landing Page Builder", applicationCategory: "BusinessApplication", description: "Create opt-in landing pages with forms that add subscribers directly to your email list.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeatureHero badge="Lead Generation" icon={Layout}
        title="Landing Pages That" highlight="Convert"
        description="Build opt-in pages in minutes — not hours. Pick a template, customize the content and colors, publish, and start capturing leads directly into your email list." />

      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Everything You <span className="text-coral">Need</span>
          </h2>
          <FeatureGrid items={[
            { title: "6 Proven Templates", desc: "Lead magnet, webinar registration, waitlist, newsletter signup, discount offer, or blank canvas. Start fast.", icon: Palette },
            { title: "Instant Publishing", desc: "One click to publish. Your page goes live at a clean URL immediately. One click to unpublish.", icon: Globe },
            { title: "Auto-Add Subscribers", desc: "Form submissions automatically create subscribers in your list — with custom tags for segmentation.", icon: Users },
            { title: "Conversion Tracking", desc: "See views, submissions, and conversion rate for every page. Know what's working.", icon: BarChart3 },
            { title: "Custom Styling", desc: "Background color, button color, text color — match your brand without writing CSS.", icon: Palette },
            { title: "Live Preview", desc: "See changes in real-time as you edit. What you see is what your visitors get.", icon: Eye },
          ]} />
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-6">Templates for Every Use Case</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { name: "Lead Magnet", desc: "Free resource download" },
              { name: "Webinar Registration", desc: "Event signups" },
              { name: "Waitlist", desc: "Coming soon pages" },
              { name: "Newsletter Signup", desc: "Subscription pages" },
              { name: "Discount / Coupon", desc: "Offer-gated signups" },
              { name: "Blank Canvas", desc: "Start from scratch" },
            ].map(t => (
              <div key={t.name} className="bg-paper rounded-xl p-4 border border-sand text-center">
                <p className="text-sm font-bold text-ink">{t.name}</p>
                <p className="text-[10px] text-slate mt-0.5">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureCta title="Build Your First Page in 60 Seconds"
        desc="Pick a template, write your headline, customize colors, publish. Leads flow directly into your subscriber list with tags." />
    </>
  );
}
